import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface NewsletterProps {
  language: string;
}

const Newsletter: React.FC<NewsletterProps> = ({ language }) => {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const content = {
    en: {
      title: "Join our Beauty Community",
      subtitle: "Get free medical info about your skin, lips, and more. Join our newsletter to receive new offers, services, and discounts.",
      fullNameLabel: "Full Name",
      fullNamePlaceholder: "Enter your full name",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email",
      submitButton: "Subscribe Now",
      success: {
        title: "Thank You!",
        message: "You've successfully joined our beauty community."
      },
      error: {
        required: "All fields are required",
        invalidEmail: "Please enter a valid email address",
        duplicate: "This email is already subscribed",
        generic: "Something went wrong. Please try again."
      }
    },
    da: {
      title: "Bliv en del af vores skønhedsfællesskab",
      subtitle: "Få gratis medicinsk info om din hud, dine læber og meget mere. Tilmeld dig vores nyhedsbrev og modtag nye tilbud, services og rabatter.",
      fullNameLabel: "Fulde navn",
      fullNamePlaceholder: "Indtast dit fulde navn",
      emailLabel: "E-mailadresse",
      emailPlaceholder: "Indtast din e-mail",
      submitButton: "Tilmeld dig nu",
      success: {
        title: "Tak!",
        message: "Du er nu tilmeldt vores skønhedsfællesskab."
      },
      error: {
        required: "Alle felter skal udfyldes",
        invalidEmail: "Indtast venligst en gyldig e-mailadresse",
        duplicate: "Denne e-mail er allerede tilmeldt",
        generic: "Noget gik galt. Prøv venligst igen."
      }
    }
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate required fields
    if (!email || !fullName) {
      setError(content[language as keyof typeof content].error.required);
      setIsSubmitting(false);
      return;
    }

    // Validate email format
    if (!validateEmail(email)) {
      setError(content[language as keyof typeof content].error.invalidEmail);
      setIsSubmitting(false);
      return;
    }

    try {
      const { error: dbError } = await supabase
        .from('newsletter_info')
        .insert([{ 
          email_address: email, 
          full_name: fullName,
          subscription_status: 'active'
        }]);

      if (dbError) {
        // Handle duplicate email error
        if (dbError.code === '23505') {
          setError(content[language as keyof typeof content].error.duplicate);
        } else {
          console.error('Database error:', dbError);
          setError(content[language as keyof typeof content].error.generic);
        }
        return;
      }

      setIsSubmitted(true);
      setEmail('');
      setFullName('');
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      setError(content[language as keyof typeof content].error.generic);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(166,139,124,0.1),transparent)]" />
      
      <div className="container mx-auto px-4 relative">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 
              className="text-3xl md:text-4xl font-serif font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as keyof typeof content].title}
            </h2>
            <p className="text-lg text-gray-600">
              {content[language as keyof typeof content].subtitle}
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-px rounded-xl bg-gradient-to-r from-transparent via-[#A68B7C]/30 to-transparent" />
            <div className="absolute -inset-px rounded-xl bg-gradient-to-b from-white/50 to-white/10 backdrop-blur-sm" />

            <div className="relative bg-white/80 backdrop-blur-md rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-[#A68B7C]/20">
              {isSubmitted ? (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                    <svg className="w-6 h-6 text-green-600\" fill="none\" stroke="currentColor\" viewBox="0 0 24 24">
                      <path strokeLinecap="round\" strokeLinejoin="round\" strokeWidth="2\" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 
                    className="text-xl font-serif font-semibold mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {content[language as keyof typeof content].success.title}
                  </h3>
                  <p className="text-gray-600">
                    {content[language as keyof typeof content].success.message}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm">
                      {error}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium mb-2">
                        {content[language as keyof typeof content].fullNameLabel}
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C] transition-all duration-300"
                        placeholder={content[language as keyof typeof content].fullNamePlaceholder}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        {content[language as keyof typeof content].emailLabel}
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C] transition-all duration-300"
                        placeholder={content[language as keyof typeof content].emailPlaceholder}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-[#A68B7C] text-white font-medium rounded-md hover:bg-[#95796A] transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-70 flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white\" xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24">
                          <circle className="opacity-25\" cx="12\" cy="12\" r="10\" stroke="currentColor\" strokeWidth="4" />
                          <path className="opacity-75\" fill="currentColor\" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Send size={18} className="mr-2" />
                        {content[language as keyof typeof content].submitButton}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;